import React from 'react';
import { DatePicker, Form, Input, Modal, Select, Spin } from 'antd';
import { DOCUMENT_UI_DATE_FORMAT, DOCUMENT_UI_DATETIME_FORMAT } from '../../../../utils/documentUiFormat';
import { CLOSING_PACKAGE_MODAL_TITLES } from '../../workspace/closingPackageModalKinds';
import { useSendOriginalsModal } from '../../hooks/useSendOriginalsModal';
import { ORIGINALS_DISPATCH_METHOD_OPTIONS } from '../../lib/sendOriginalsModalValidation';
import ClosingPackageTaskModalContext from './ClosingPackageTaskModalContext';
import ClosingPackageTaskModalEventDateHint from './ClosingPackageTaskModalEventDateHint';
import ClosingPackageTaskModalFooter from './ClosingPackageTaskModalFooter';
import type { ClosingPackageTaskModalBaseProps } from './ClosingPackageFormationModal.types';

export type ClosingPackageSendOriginalsModalProps = ClosingPackageTaskModalBaseProps & {
  onSuccess?: () => void | Promise<void>;
};

/**
 * IMP-001H-5A — SEND_ORIGINALS workspace modal (paper originals dispatch).
 */
const ClosingPackageSendOriginalsModal: React.FC<ClosingPackageSendOriginalsModalProps> = ({
  open,
  onClose,
  applicationId,
  onSuccess,
}) => {
  const { form, projection, contextView, contextLoading, submitting, handleSubmit } =
    useSendOriginalsModal({
      applicationId,
      open,
      onClose,
      onSuccess,
    });

  return (
    <Modal
      title={CLOSING_PACKAGE_MODAL_TITLES['send-originals']}
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={760}
      data-testid="send-originals-modal"
      footer={
        <ClosingPackageTaskModalFooter
          onClose={onClose}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Зарегистрировать отправку"
          submitTestId="send-originals-submit"
        />
      }
    >
      {contextLoading || !projection ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <>
          <ClosingPackageTaskModalContext contextView={contextView} />

          <Form form={form} layout="vertical">
            <Form.Item
              name="dispatchedAt"
              label="Дата отправки"
              rules={[{ required: true, message: 'Укажите дату отправки' }]}
            >
              <DatePicker
                showTime
                style={{ width: '100%' }}
                data-testid="send-originals-date"
                format={DOCUMENT_UI_DATETIME_FORMAT}
              />
            </Form.Item>

            <Form.Item
              name="dispatchMethod"
              label="Способ доставки"
              rules={[{ required: true, message: 'Укажите способ доставки' }]}
            >
              <Select
                options={ORIGINALS_DISPATCH_METHOD_OPTIONS}
                placeholder="Выберите способ доставки"
                data-testid="send-originals-method"
              />
            </Form.Item>

            <Form.Item
              name="trackingNumber"
              label="Трек-номер"
              rules={[
                { required: true, message: 'Укажите трек-номер' },
                { whitespace: true, message: 'Трек-номер не может быть пустым' },
              ]}
            >
              <Input placeholder="Номер отправления" data-testid="send-originals-tracking" />
            </Form.Item>

            <Form.Item
              name="estimatedReceiptAt"
              label="Ориентировочная дата получения"
              rules={[{ required: true, message: 'Укажите ориентировочную дату получения' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                data-testid="send-originals-estimated-receipt"
                format={DOCUMENT_UI_DATE_FORMAT}
              />
            </Form.Item>

            <Form.Item
              name="comment"
              label="Комментарий"
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder="Комментарий для журнала комплекта (необязательно)"
                data-testid="send-originals-comment"
              />
            </Form.Item>
          </Form>

          <ClosingPackageTaskModalEventDateHint />
        </>
      )}
    </Modal>
  );
};

export default ClosingPackageSendOriginalsModal;
